import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { ClinicPimsIntegrationType } from 'shared/types/graphql';
import { integrationSystemDisplayName, useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { updateAppointmentModalErrors } from 'pages/Appointments/state/appointmentsModalSlice';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';

interface ValidateAppointmentDraftReturn {
  validateAppointmentDraft: () => number;
}

export type AppointmentError =
  | 'missingPet'
  | 'missingProvider'
  | 'missingRoom'
  | 'missingAppointmentType'
  | 'missingStartTime'
  | 'missingEndTime'
  | 'missingPetParent';

export const useValidateAppointmentDraft = (): ValidateAppointmentDraftReturn => {
  const {
    appointmentDraft: { selectedPet, petId, petParentIds, durationInMinutes, roomId, clinicEmployeeId, startAt },
  } = useAppSelector((state) => state.appointmentsModal);
  const { primaryIntegration, primaryIntegrationName } = useIntegrationsProvider();
  const primaryIntegrationType = primaryIntegration?.type || null;
  const dispatch = useAppDispatch();
  const { modules } = useModularity();
  const validateAppointmentDraft = useCallback(() => {
    const _errors: AppointmentError[] = [];

    // Ensure a valid date has been selected
    if (!startAt) {
      _errors.push('missingStartTime');
    }

    if (modules?.includes(ModuleName.AppointmentsStandard)) {
      // Ensure a pet has been selected
      if (!selectedPet || !petId) {
        _errors.push('missingPet');
      }

      // Ensure at least one pet parent is selected
      if (!petParentIds?.length) {
        _errors.push('missingPetParent');
      }
      // Ensure a valid end time has been selected
      if (!durationInMinutes || durationInMinutes < 0) {
        _errors.push('missingEndTime');
      }

      // Ensure a room resource has been selected for any PIMS outside of Ezyvet, Vetdata, and Datapoint
      if (
        primaryIntegrationType !== ClinicPimsIntegrationType.Ezyvet &&
        primaryIntegrationType !== ClinicPimsIntegrationType.Vetdata &&
        primaryIntegrationType !== ClinicPimsIntegrationType.Datapoint
      ) {
        if (!roomId) {
          _errors.push('missingRoom');
        }
      }

      // Add checks for EzVet required fields
      if (primaryIntegrationType === ClinicPimsIntegrationType.Ezyvet && !clinicEmployeeId && !roomId) {
        _errors.push('missingRoom', 'missingProvider');
      }

      // Ensure an employee has been selected for any PIMS outside of Ezyvet and Avimark
      if (
        !clinicEmployeeId &&
        primaryIntegrationType !== ClinicPimsIntegrationType.Ezyvet &&
        primaryIntegrationName !== integrationSystemDisplayName.AVIMARK
      ) {
        _errors.push('missingProvider');
      }
    }

    dispatch(updateAppointmentModalErrors(_errors));

    return _errors.length;
  }, [
    selectedPet,
    petId,
    petParentIds?.length,
    startAt,
    durationInMinutes,
    modules,
    dispatch,
    primaryIntegrationType,
    clinicEmployeeId,
    roomId,
    primaryIntegrationName,
  ]);

  return {
    validateAppointmentDraft,
  };
};
